import { styled, Tooltip, Typography } from 'opensolar-ui';
import React from 'react';
import { useTranslate } from 'react-admin';

const PriceLabel = styled('span')<{ size?: 'small' | 'default'; labelStyle?: React.CSSProperties }>(
  ({ theme, size = 'default', labelStyle }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: theme.spacing(0.5),
    color: theme.palette.neutralGrey[1000],
    fontSize: size === 'small' ? 12 : 16,
    '&:hover': {
      textDecoration: 'underline',
    },
    ...labelStyle,
  })
)

const IconBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.neutralGrey[300],
  borderRadius: 4,
  width: 20,
  height: 20,
}))

const DarkTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[1300],
}))

const LinkText = styled('span')(({ theme }) => ({
  color: theme.palette.blue[800],
  cursor: 'pointer',
  textDecoration: 'underline',
  display: 'inline-block',
  marginTop: theme.spacing(0.5),
  fontWeight: theme.typography.fontWeightRegular,
}))

const ImageWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  maxHeight: 168,
  maxWidth: 300,
  borderRadius: 4,
  paddingBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 4,
  },
}))

type TooltipContentProps = {
  imageUrl?: string
  imageAlt?: string
  heading: string
  body: string
  linkText?: string
  onLinkClick?: (e: React.MouseEvent) => void
}

const TooltipBody: React.FC<TooltipContentProps> = ({ imageUrl, imageAlt, heading, body, linkText, onLinkClick }) => {
  const translate = useTranslate()

  return (
    <div onClick={(e) => { e.stopPropagation(); e.preventDefault() }}>
      {imageUrl && (
        <ImageWrapper>
          <img src={imageUrl} alt={imageAlt} />
        </ImageWrapper>
      )}
      <DarkTypography textVariant="caption1Bold">
        {translate(heading)}
      </DarkTypography>
      <DarkTypography textVariant="caption1">
        {translate(body)}
      </DarkTypography>
      {linkText && onLinkClick && (
        <LinkText onClick={onLinkClick}>
          {translate(linkText)}
        </LinkText>
      )}
    </div>
  )
}

export type SupplierIntegrationPricingLabelProps = {
  variant?: 'default' | 'small'
  labelStyle?: React.CSSProperties
  labelText: string
  icon: React.ReactNode
  tooltipImageUrl?: string
  tooltipImageAlt?: string
  tooltipHeading: string
  tooltipBody: string
  tooltipLinkText?: string
  onLinkClick?: (e: React.MouseEvent) => void
  iconBadgeStyle?: React.CSSProperties
}

const SupplierIntegrationPricingLabel: React.FC<SupplierIntegrationPricingLabelProps> = ({
  variant = 'default',
  labelStyle,
  iconBadgeStyle,
  labelText,
  icon,
  tooltipImageUrl,
  tooltipImageAlt = 'Pricing',
  tooltipHeading,
  tooltipBody,
  tooltipLinkText,
  onLinkClick,
}) => {
  const translate = useTranslate()

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <Tooltip
      title={
        <TooltipBody
          imageUrl={tooltipImageUrl}
          imageAlt={tooltipImageAlt}
          heading={tooltipHeading}
          body={tooltipBody}
          linkText={tooltipLinkText}
          onLinkClick={onLinkClick}
        />
      }
      placement="top"
      arrow
      interactive
    >
      <PriceLabel size={variant} labelStyle={labelStyle} onClick={stopPropagation}>
        {translate(labelText)}
        <IconBadge style={iconBadgeStyle}>{icon}</IconBadge>
      </PriceLabel>
    </Tooltip>
  )
}

export default SupplierIntegrationPricingLabel
